/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { useParams } from "react-router-dom";
import { Spacer, Tab, Tabs, theme } from "@nordcloud/gnui";
import { CostBox } from "~/components/CostBox";
import { useCurrency } from "~/hooks";
import { useDisclosure } from "~/hooks/useDisclosure/useDisclosure";
import { CloudAccountStatus, isNotEmpty, isNotNil, noop } from "~/tools";
import { AccountCredentialsUpdateSidebar } from "../AccountCredentialsUpdateSidebar/AccountCredentialsUpdateSidebar";
import { ChargeTypeCostsBar } from "../components/ChargeTypeCostsBar";
import { ClusterUtilization } from "../components/ClusterUtilization";
import {
  getAccountDisplayName,
  isKubernetesOrOpenshift,
  mapProviderTab,
} from "../utils";
import {
  Details,
  DetailsForm,
  DetailsHeader,
  DetailsNotification,
} from "./Details";
import { Header } from "./Header";
import { EstateRecords } from "./Tabs";
import { Account } from "./types";
import { useCloudAccountDetails } from "./useCloudAccountDetails";

export function CloudAccountDetailsPage() {
  const {
    open: openCredentialsUpdateSteps,
    close: closeCredentialsUpdateSteps,
    isOpen: showCredentialsUpdateSteps,
  } = useDisclosure();

  const { nid } = useParams<RouteParams>();

  const {
    account,
    tags,
    showUpdateCredentials,
    chargeTypeCosts,
    accountSavingsCosts,
    accountSpends,
  } = useCloudAccountDetails(nid);

  const { currency } = useCurrency();

  let notification = null;
  if (isNotNil(account?.providerType) && isNotNil(account?.cloudStatus)) {
    const accountProps: Account = {
      id: account.id,
      name: account.name,
      cloudStatus: account.cloudStatus,
      provider: account.provider,
      providerType: account.providerType,
      metadata: account.metadata ?? undefined,
      owner: account.owner ?? undefined,
    };
    notification = (
      <DetailsNotification
        openCredentialsUpdateSteps={openCredentialsUpdateSteps}
        account={accountProps}
      />
    );
  }

  const showClusterUtilization = isNotNil(account?.providerType)
    ? isKubernetesOrOpenshift(account?.providerType)
    : false;

  const providerTab = mapProviderTab(account?.provider, account?.providerType);

  return (
    <>
      <When condition={isNotNil(account)}>
        <Header
          tags={tags}
          account={account}
          openCredentialsUpdateSteps={openCredentialsUpdateSteps}
          showUpdateCredentials={showUpdateCredentials}
        />
        {notification}
        <Details>
          <div css={{ width: "100%" }}>
            <DetailsHeader
              accountName={getAccountDisplayName(account)}
              provider={account?.provider}
              providerType={account?.providerType}
              cloudStatus={account?.cloudStatus ?? CloudAccountStatus.NoAccess}
            />
            <DetailsForm
              accountInternalId={nid}
              accountId={account?.providerId}
              accountOwner={account?.owner}
              description={account?.description ?? ""}
              contactsNames={(account?.contacts ?? [])
                .map((contactPerson) => contactPerson?.name ?? "")
                .join(", ")}
            />
          </div>
          <div css={{ width: "70%", height: "18.5rem" }}>
            <CostBox
              numericalValues={{
                forecast: accountSpends.currentMonthForecastValue,
                currentMonth: accountSpends.currentMonthValue,
                previousMonth: accountSpends.previousMonthValue,
                currency,
                budget: 0,
                cloudWasteCost: accountSavingsCosts.cost,
                co2e: accountSpends.currentMonthCo2e,
                co2eSavings: accountSavingsCosts.co2e,
                originalCost: 0,
                reservedInstanceBenefit: 0,
              }}
            />
          </div>
        </Details>
        <When condition={isNotEmpty(chargeTypeCosts)}>
          <Spacer height={theme.spacing.spacing04} />
          <ChargeTypeCostsBar chargeTypeCosts={chargeTypeCosts} />
        </When>
        <When condition={showClusterUtilization}>
          <Spacer height={theme.spacing.spacing04} />
          <ClusterUtilization nordcloudId={account?.nid} />
        </When>
        <Spacer height={theme.spacing.spacing04} />
        <Tabs handleTab={noop} step={0}>
          <Tab heading="Estate Records" caption="Account estate records">
            <EstateRecords
              providerId={account?.providerId}
              providerType={account?.providerType ?? undefined}
              provider={account?.provider}
            />
          </Tab>
        </Tabs>
        <When condition={showUpdateCredentials && isNotNil(providerTab)}>
          {providerTab && (
            <AccountCredentialsUpdateSidebar
              nids={[nid]}
              provider={providerTab}
              isOpen={showCredentialsUpdateSteps}
              onClose={closeCredentialsUpdateSteps}
            />
          )}
        </When>
      </When>
    </>
  );
}

type RouteParams = {
  nid: string;
};
